import { useMemo } from 'react';

import { ErrorCode } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { typography } from '@pulse-web-ui/theme';

import { FileInputController, PageHeader } from '@shared/index';

import type { SubmitProfileEditValues } from '../../../../model';
import {
  Container,
  Description,
  HintContainer,
  LabelHighlight,
  PrimaryText,
} from './attach-document.styles';

const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/tiff': ['tif'],
  'image/jpeg': ['jpeg'],
  'application/pdf': ['.pdf'],
};

interface AttachDocumentsProps {
  documents: string;
}

export const AttachDocuments = ({ documents }: AttachDocumentsProps) => {
  const { t } = useTranslation();

  const { control } = useFormContext<SubmitProfileEditValues>();

  const filesErrorMessages = useMemo(
    () => ({
      [ErrorCode.FileTooLarge]: (name: string) =>
        t('AGENT_PROFILE:errors.fileTooLarge', { name }),
      [ErrorCode.FileInvalidType]: (name: string) =>
        t('AGENT_PROFILE:errors.fileInvalidType', { name }),
    }),
    [t]
  );

  return (
    <Container>
      <PageHeader
        $fontSize={typography.medium20}
        $marginBottom={8}
      >
        {t('AGENT_PROFILE:headers.everythingIsRight')}
      </PageHeader>
      <Description>
        <Trans
          components={{ primary: <PrimaryText /> }}
          values={{ documents }}
        >
          {t('AGENT_PROFILE:hints.attachDocuments')}
        </Trans>
      </Description>
      <FileInputController
        control={control}
        name='files'
        label={
          <div>
            <Trans components={{ highlight: <LabelHighlight /> }}>
              {t('AGENT_PROFILE:labels.files')}
            </Trans>
          </div>
        }
        hint={
          <HintContainer>
            <div>{t('AGENT_PROFILE:hints.filesFormat')}</div>
            <div>{t('AGENT_PROFILE:hints.filesSize')}</div>
          </HintContainer>
        }
        acceptedTypes={ACCEPTED_FILE_TYPES}
        maxFiles={5}
        maxFileSize={2}
        errorMessages={filesErrorMessages}
      />
    </Container>
  );
};
